import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { OrdersPresenter } from './orders-presenter'
import { orderServices } from '../../services'
import { useNavigate } from 'react-router-dom'
import { EngagementEvents, logCrmEvent } from '../../lib/utils/crmEvents'
import { RootState } from '../../store'
import { showBanner } from '../../global-state/banner-slice'
import {
  accessMeetsRequired,
  normalizePermissionsFromLogin,
} from '../../lib/permissions/adminFeatures'
import { INSUFFICIENT_PERMISSION_MESSAGE } from '../../lib/permissions/uiMessages'

const OrdersContainer = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { role, permissions } = useSelector((state: RootState) => state.gbcUser)
  const effectivePermissions = useMemo(
    () => permissions ?? normalizePermissionsFromLogin(role as string, null),
    [permissions, role],
  )
  const [searchParams, setSearchParams] = useState<{
    limit: string
    offset: string
    keyword: string
    filterBy: string
    orderStatus: string
    sortBy: string
  }>({
    limit: '10',
    offset: '0',
    keyword: '',
    filterBy: '',
    orderStatus: '',
    sortBy: '',
  })
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 10,
    count: 5,
    offset: 0,
  })
  const [currentPage, setCurrentPage] = useState(1)
  const [orders, setOrders] = useState<any>([])

  useEffect(() => {
    const fetchOrders = async () => {
      const data: any = await orderServices.getOrders(searchParams)
      if (data.status === 200) {
        setOrders(data.orders)
        setPagination(data.pagination)
      }
    }
    fetchOrders()
  }, [currentPage, searchParams])

  const handleSearchParams = async ({ name, value }: { name: string; value: string }) => {
    const { limit } = pagination
    setSearchParams({
      ...searchParams,
      offset: '0',
      limit: limit.toString(),
      [name]: value,
    })
    setCurrentPage(1)
    setPagination({ ...pagination, offset: 0 })
  }

  const resetSearchParams = async () => {
    setCurrentPage(1)
    setSearchParams({
      limit: '10',
      offset: '0',
      keyword: '',
      filterBy: '',
      orderStatus: '',
      sortBy: '',
    })
    setPagination((prev) => ({ ...prev, limit: 10, offset: 0 }))
  }

  const handleCurrentPage = (currentPage: number) => {
    const { limit } = pagination
    setCurrentPage(currentPage)
    setSearchParams({
      ...searchParams,
      offset: `${(currentPage - 1) * limit}`,
    })
    setPagination({ ...pagination, offset: (currentPage - 1) * limit })
  }

  const handleOrderClick = (
    opportunitySlug: string,
    opportunityId: number | string,
    investorId: number | string,
    orderId: number | string,
    orderType: string,
  ) => {
    const needsInvestmentWrite = orderType === 'BUY'
    const needsWithdrawalWrite = orderType === 'SELL'
    if (needsInvestmentWrite && !accessMeetsRequired(effectivePermissions.investments, 'write')) {
      dispatch(
        showBanner({
          text: INSUFFICIENT_PERMISSION_MESSAGE,
          variant: 'permission-denied-banner',
        }),
      )
      return
    }
    if (needsWithdrawalWrite && !accessMeetsRequired(effectivePermissions.withdrawals, 'write')) {
      dispatch(
        showBanner({
          text: INSUFFICIENT_PERMISSION_MESSAGE,
          variant: 'permission-denied-banner',
        }),
      )
      return
    }

    logCrmEvent(EngagementEvents.CLICKED_VIEW_ORDER, {
      page: 'orders',
      orderId,
      investorId,
      orderType,
    })
    if (orderType === 'BUY') {
      navigate(
        `/opportunity/${opportunitySlug}/${opportunityId}/client/${investorId}/investment/detail/${orderId}`,
      )
    } else if (orderType === 'SELL') {
      navigate(
        `/opportunity/${opportunitySlug}/${opportunityId}/client/${investorId}/withdrawal/detail/${orderId}`,
      )
    }
  }

  return (
    <OrdersPresenter
      pagination={pagination}
      handleCurrentPage={handleCurrentPage}
      currentPage={currentPage}
      resetSearchParams={resetSearchParams}
      searchParams={searchParams}
      orders={orders}
      handleSearchParams={handleSearchParams}
      handleOrderClick={handleOrderClick}
    />
  )
}

export { OrdersContainer }
